<script>
import { mapStores } from 'pinia'

import api from '@/cartapi.js'
import { useCartStore } from '@/cart/stores/cart'
import CartEventHero from '@/cart/components/CartEventHero.vue'
import { getMinConsumerPrice } from '@/cart/utils/pricing.js'

export default {
  name: 'EventLocationsView',

  components: {
    CartEventHero,
  },

  props: {
    eventSlug: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      error: null,
      eventEnded: false,
      faqs: [
        {
          id: 'features',
          question: 'What are the features of the charging lockers?',
          answer:
            'Lockers include power for device charging, secure combination locks, and weather protection.',
          open: false,
        },
        {
          id: 'items',
          question: 'What items can I securely store?',
          answer:
            'Most personal items such as phones, wallets, small bags, and jackets. Hazardous items are not permitted.',
          open: false,
        },
        {
          id: 'access',
          question: 'How do I retrieve my belongings from the locker?',
          answer:
            'You will receive access instructions with your order confirmation and can access your locker during event hours.',
          open: false,
        },
      ],
    }
  },

  computed: {
    ...mapStores(useCartStore),

    currentEvent() {
      return this.cartStore.selectedEvent
    },

    // Check if we're in preview mode
    isPreviewMode() {
      return this.cartStore.isPreviewMode
    },

    // Get preview token from URL query params
    previewToken() {
      return this.$route.query.preview || null
    },

    // Build locations from products grouped by location, enriched with metadata
    locations() {
      const byLocation = this.cartStore.byLocation || {}
      const locationsMeta = this.cartStore.locationsMeta || []

      // Create a lookup map for location metadata by id
      const metaById = {}
      for (const loc of locationsMeta) {
        metaById[loc.id] = loc
      }

      return Object.entries(byLocation).map(([location, products]) => {
        // Get location metadata if available
        const meta = metaById[location] || {}

        // Get min consumer price using validated utility
        // Utility logs errors and returns hasPricingError if consumerPriceCents is missing
        let minPriceCents = 0
        let hasPricingError = false

        for (const product of products) {
          const priceResult = getMinConsumerPrice(product)
          if (!priceResult.hasValidPricing) {
            hasPricingError = true
            break // One invalid product invalidates the location
          }
          if (minPriceCents === 0 || priceResult.minPriceCents < minPriceCents) {
            minPriceCents = priceResult.minPriceCents
          }
        }

        const offeringCount = products.length

        // Normalize warnings to list[str]
        const rawWarnings = meta.warnings || null
        const warnings = rawWarnings
          ? (Array.isArray(rawWarnings) ? rawWarnings : [rawWarnings]).filter(Boolean)
          : null

        return {
          slug: location.toLowerCase().replace(/\s+/g, '-'),
          id: location,
          name: meta.name || location,
          description: meta.description || `${offeringCount} locker option${offeringCount !== 1 ? 's' : ''} available`,
          warnings,
          minPriceCents: hasPricingError ? null : minPriceCents,
          hasPricingError,
          products,
        }
      })
    },

    commSettings() {
      return this.cartStore.commSettings || {}
    },
  },

  async created() {
    try {
      // Get preview token from URL if present
      const previewToken = this.previewToken

      // Fetch products from catalog API (vendor derived from Host header)
      const data = await api.catalog.listProducts(this.eventSlug, previewToken)

      if (!data || !data.event) {
        this.error = 'We could not find this event.'
        return
      }

      if (data.message) {
        this.error = data.message
        this.loading = false
        return
      }

      // Store event and products
      this.cartStore.setSelectedEvent(data.event)
      this.cartStore.setProducts(data)

      // Track preview mode status
      if (data.isPreview) {
        this.cartStore.setPreviewMode(true)
      }

      // Check if event is still purchasable (skip in preview — purchasable is always false there)
      if (!data.isPreview && data.event && data.event.purchasable === false) {
        this.eventEnded = true
        this.loading = false
        return
      }

      if (!data.products || data.products.length === 0) {
        this.error = 'No lockers are currently available for this event.'
        return
      }
    } catch (err) {
      console.error('Failed to load event products', err)
      if (err.response?.status === 404) {
        this.error = 'This event is not currently available for purchase.'
      } else {
        this.error = 'Unable to load this event right now. Please try again.'
      }
    } finally {
      this.loading = false
    }
  },

  methods: {
    selectLocation(location) {
      if (!this.currentEvent || !this.eventSlug || !location || !location.slug) {
        return
      }

      // Preserve preview token in navigation
      const query = this.previewToken ? { preview: this.previewToken } : {}

      this.$router.push({
        name: 'ConfigureLocker',
        params: {
          eventSlug: this.eventSlug,
          locationSlug: location.slug,
        },
        query,
      })
    },
  },
}
</script>

<template>
  <section class="section event-locations">
    <CartEventHero />

    <div class="container">
      <div class="columns is-variable is-6">
        <div class="column is-12-tablet is-8-desktop">
          <div class="locations-header">
            <h2 class="title is-4">Choose Your Location</h2>
            <p class="subtitle is-6">
              Keep your belongings safe and secure during the festival. All lockers include weather
              protection and security features.
            </p>
          </div>

          <div v-if="eventEnded" class="notification is-info is-light">
            <p class="has-text-weight-semibold mb-1">This event has ended</p>
            <p class="is-size-7">
              Locker reservations are no longer available for this event.
              If you have an existing reservation, check your confirmation email for details.
            </p>
          </div>

          <div v-else-if="error" class="notification is-danger">
            {{ error }}
          </div>

          <div v-else>
            <div v-if="loading" class="has-text-centered py-5">Loading…</div>

            <div v-else class="columns is-multiline is-mobile">
              <div
                v-for="location in locations"
                :key="location.slug"
                class="column is-12-mobile is-4-desktop"
              >
                <div class="card location-card">
                  <div class="location-card__body">
                    <p class="title is-6 mb-3">
                      {{ location.name }}
                    </p>

                    <div class="location-card__icon">
                      <b-icon icon="map-marker" size="is-large" />
                    </div>

                    <p class="is-size-7 has-text-grey mb-2">
                      {{ location.description }}
                    </p>

                    <div v-if="location.warnings && location.warnings.length" class="location-card__warning mb-2">
                      <p v-for="(warn, idx) in location.warnings" :key="idx" class="is-size-7">
                        <b-icon icon="alert-circle-outline" size="is-small" class="mr-1" />
                        {{ warn }}
                      </p>
                    </div>

                    <p v-if="location.hasPricingError" class="has-text-danger is-size-7">
                      <b-icon icon="alert-circle" size="is-small" class="mr-1" />
                      Price unavailable
                    </p>
                    <p v-else-if="location.minPriceCents" class="has-text-weight-semibold">
                      From ${{ (location.minPriceCents / 100).toFixed(2) }}
                    </p>
                  </div>

                  <div class="card-footer location-card__footer">
                    <div class="card-footer-item">
                      <button
                        type="button"
                        class="button is-text is-small location-card__cta"
                        :disabled="location.hasPricingError"
                        @click="selectLocation(location)"
                      >
                        <span>{{ location.hasPricingError ? 'Unavailable' : 'Select Location' }}</span>
                        <b-icon v-if="!location.hasPricingError" icon="arrow-right" size="is-small" class="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="faq-section">
        <h2 class="title is-5">Frequently Asked Questions</h2>

        <!-- Vendor-provided FAQ content (raw text for now; markdown support planned) -->
        <div v-if="commSettings.faqContent" class="content">
          <p style="white-space: pre-wrap">{{ commSettings.faqContent }}</p>
        </div>

        <!-- Default FAQs fallback -->
        <div v-else class="faq-list">
          <div v-for="faq in faqs" :key="faq.id" class="faq-item">
            <div
              class="faq-item__header"
              :class="{ 'is-open': faq.open }"
              @click="faq.open = !faq.open"
            >
              <span>{{ faq.question }}</span>
              <b-icon :icon="faq.open ? 'chevron-up' : 'chevron-down'" size="is-small" />
            </div>
            <div v-show="faq.open" class="faq-item__body">
              <p class="is-size-7 has-text-grey">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.event-locations {
  padding-top: 1.5rem;
}

@media (min-width: 769px) {
  .event-locations {
    padding-top: 2rem;
  }
}
.locations-header {
  margin-bottom: 1.5rem;
}

.location-card {
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.location-card__body {
  padding: 1.25rem 1.25rem 0.75rem;
}

.location-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin-bottom: 0.75rem;
  border-radius: 16px;
  background-color: #eef2ff;
  color: #1d4ed8;
}

.location-card__footer {
  margin-top: auto;
}

.location-card__cta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 0;
  padding-right: 0;
}

.faq-section {
  margin-top: 3rem;
}

.location-card__warning {
  background-color: #fff8e1;
  border-left: 3px solid #ffc107;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  color: #856404;
}

.faq-list {
  border-radius: 8px;
}

.faq-item__header {
  padding: 0.75rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
}

.faq-item__header.is-open {
  font-weight: 600;
}

.faq-item__body {
  padding-bottom: 0.75rem;
}

</style>